// Right-sized catalogue images through Cloudflare image transformations
// (speed Phase 1, 2026-10-09). See ops/2026-10-08-phase1-image-cdn-prep.md.
//
// OFF by default, and while it is off every function here returns exactly
// what it was given: merging this changes no URL on the site. It is turned
// on only once images.emporia.ke exists as an R2 custom domain with
// transformations enabled — before that, every rewritten URL would 404.
//
// Two rules keep the Cloudflare bill at $0 and must not be relaxed:
//  * ONLY widths on LADDER. Cloudflare bills each (image, size) pair once a
//    month, so a width computed from the screen would make every device a
//    new billable transformation. The 5,000 free ones cover the catalogue
//    up to ~430 products at three sizes each.
//  * onerror=redirect on every URL, so if the free allowance ever runs out
//    a shopper gets the original image, not a broken one (Cloudflare
//    answers a new size with error 9422 once it is exhausted).
//
// format=auto serves AVIF to browsers that accept it, then WebP, then the
// original format — and it counts as ONE transformation, not one per format.

// ON since 2026-10-13: images.emporia.ke is connected to the R2 bucket and
// transformations are enabled for the zone (owner, verified: AVIF 66 KB for
// the 1.8 MB hero, foreign origins refused 403).
export const IMAGE_CDN_ENABLED = true;
export const IMAGE_CDN_ORIGIN = "https://images.emporia.ke";
const QUALITY = 75;

// Where our catalogue images live. Anything else (Netlify-hosted category
// art, a pasted external URL, a data: or blob: preview) passes through.
const R2_PUBLIC_RE = /^https:\/\/pub-[a-z0-9]+\.r2\.dev\/(.+)$/;
const CDN_RE = /^https:\/\/images\.emporia\.ke\/(?!cdn-cgi\/)(.+)$/;

export const LADDER = Object.freeze({
  product: [480, 960, 1600],
  hero: [960, 1600, 2400],
  tile: [240, 480],
});
const ALLOWED = new Set(Object.values(LADDER).flat());

let enabled = IMAGE_CDN_ENABLED;
/** Test seam. Production code never calls this. */
export const _setImageCdn = (on) => { enabled = Boolean(on); };

/** The object key of a catalogue image, or null if it is not one of ours. */
export function r2Key(src) {
  if (typeof src !== "string") return null;
  const m = src.match(R2_PUBLIC_RE) || src.match(CDN_RE);
  if (!m) return null;
  // Keys are plain paths (products/<uuid>.png). Refuse anything that could
  // smuggle options into the transformation URL.
  return /^[A-Za-z0-9._\-/]+$/.test(m[1]) ? m[1] : null;
}

/** One image at one ladder width; `src` unchanged when off or not ours. */
export function imageUrl(src, width) {
  if (!enabled) return src;
  if (!ALLOWED.has(width)) throw new Error(`imageUrl: ${width} is not on the ladder`);
  const key = r2Key(src);
  if (!key) return src;
  return `${IMAGE_CDN_ORIGIN}/cdn-cgi/image/width=${width},format=auto,quality=${QUALITY},onerror=redirect/${key}`;
}

/** A srcset for one kind of image, or undefined (attribute omitted). */
export function imageSrcSet(src, kind) {
  if (!enabled || !r2Key(src)) return undefined;
  return LADDER[kind].map((w) => `${imageUrl(src, w)} ${w}w`).join(", ");
}

/**
 * Everything an <img> needs: spread it in place of `src`.
 *   <img {...imageProps(product.image_url, "product", SIZES.card)} alt=… />
 * When off: exactly { src }, so the rendered markup is unchanged.
 */
export function imageProps(src, kind, sizes) {
  const srcSet = imageSrcSet(src, kind);
  if (!srcSet) return { src };
  const ladder = LADDER[kind];
  // The plain src is only for browsers without srcset: the middle rung.
  return { src: imageUrl(src, ladder[Math.min(1, ladder.length - 1)]), srcSet, sizes };
}

/*
 * The hero on PHONES (home LCP fix 2, owner's go 2026-11-05).
 *
 * On a screen up to 480px wide the hero box is the screen's width and about
 * 497px tall (Home.css: max(54vh, 497px)), and the banner is object-fit:
 * cover, centred. So a phone shows only the middle third of a 2:1 to 3:1
 * banner, yet it downloaded the whole width. Here it gets a CENTRE crop
 * instead: `fit=crop` cuts the middle out at full resolution and never
 * enlarges, so a 1774x887 banner arrives as 1000x887 (46.8 KB AVIF, was
 * 75.8 KB).
 *
 * The framing is unchanged by construction: a centred crop that is still
 * wider than the box (crop aspect >= box aspect) shows exactly the pixels the
 * full banner showed under the same cover/centre rule. 1000px of a banner at
 * most 1024px tall is wider than any phone box (at most 480/497 of the
 * height), so the condition holds for every live banner; screenshots at 360,
 * 390 and 412 for each one are in the PR.
 *
 * Two rungs, both fixed (the billing rule above): 600 for 1x screens, 1000
 * for everything sharper. `sizes` is the box height, about how wide the crop
 * is drawn.
 */
export const HERO_PHONE_MEDIA = "(max-width: 480px)";
// The preload for every other screen. NOT "not all and (max-width: 480px)":
// Chrome's preload scanner treated that as matching on a 390px phone and
// fetched the full banner as well (measured on the #251 preview, 2026-11-05).
export const HERO_WIDE_MEDIA = "(min-width: 481px)";
export const HERO_PHONE_LADDER = Object.freeze([600, 1000]);
export const HERO_PHONE_SIZES = "500px";

function heroCropUrl(key, width) {
  if (!HERO_PHONE_LADDER.includes(width)) throw new Error(`heroCropUrl: ${width} is not on the ladder`);
  return `${IMAGE_CDN_ORIGIN}/cdn-cgi/image/width=${width},height=${width},fit=crop,format=auto,quality=${QUALITY},onerror=redirect/${key}`;
}

/**
 * The phone <source> for a hero banner: { media, srcSet, sizes, src }, or
 * null when the CDN is off or the image is not ours (then no <picture> at
 * all, exactly as before). `src` is the rung a 2x+ phone picks, for the
 * preload's href.
 */
export function heroPhoneSource(src) {
  if (!enabled) return null;
  const key = r2Key(src);
  if (!key) return null;
  return {
    media: HERO_PHONE_MEDIA,
    srcSet: HERO_PHONE_LADDER.map((w) => `${heroCropUrl(key, w)} ${w}w`).join(", "),
    sizes: HERO_PHONE_SIZES,
    src: heroCropUrl(key, HERO_PHONE_LADDER[HERO_PHONE_LADDER.length - 1]),
  };
}

// `sizes` for each place an image renders: roughly how wide it is drawn, so
// the browser picks the smallest rung that is still sharp.
export const SIZES = Object.freeze({
  card: "(max-width: 700px) 50vw, 300px",
  thumb: "120px",
  mosaic: "200px",
  gallery: "(max-width: 900px) 100vw, 50vw",
  tile: "160px",
  // The hero is object-fit: cover in a box about 500px tall at a 2:1
  // aspect, so on a phone it is drawn ~1000px wide whatever the viewport.
  hero: "(max-width: 900px) 1000px, 100vw",
});

/**
 * Start (or reuse) the download an <img> with these props would make.
 * A detached Image given the same srcset and sizes picks the same candidate
 * as the real <img>, so a warm-up never fetches a different URL — which,
 * with the CDN on, would download every hero twice.
 */
export function warmImage(src, kind, sizes) {
  return new Promise((resolve) => {
    if (!src) { resolve(); return; }
    let props = imageProps(src, kind, sizes);
    // A phone shows the hero's <picture> phone source, so warm THAT file.
    const phone = kind === "hero" ? heroPhoneSource(src) : null;
    if (phone && typeof window !== "undefined" && window.matchMedia?.(phone.media).matches) props = phone;
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    if (props.srcSet) { img.sizes = props.sizes; img.srcset = props.srcSet; }
    img.src = props.src;
  });
}
